/**
 * G3-5 — Trustpilot TrustBox configuration.
 *
 * Trustpilot is already The Site Book's canonical review destination (the
 * marketing footer + /about link to uk.trustpilot.com/review/thesitebook.co.uk
 * and the cron review-ask emails point there). This adds the live TrustBox
 * widget so real reviews show as social proof on high-intent pages.
 *
 * The widget renders REAL reviews or NOTHING — it never fabricates proof. It
 * stays dark until a Trustpilot **business-unit ID** is supplied via
 * `NEXT_PUBLIC_TRUSTPILOT_BUSINESS_UNIT_ID` (same NEXT_PUBLIC_* env convention
 * as the Stripe price IDs in lib/pricing-data.ts). To switch it on:
 *   1. Trustpilot Business dashboard → Integrations → TrustBox → copy the
 *      `businessunit-id` (and, if you want a different widget style, the
 *      `template-id`).
 *   2. Set NEXT_PUBLIC_TRUSTPILOT_BUSINESS_UNIT_ID in .env.local and in
 *      do/staging.yaml + do/app.yaml (and optionally NEXT_PUBLIC_TRUSTPILOT_TEMPLATE_ID).
 * No code change is needed to go live — `hasTrustpilot()` gates every render.
 */

/** Trustpilot business-unit ID. Empty until supplied via env → widget hidden. */
export const TRUSTPILOT_BUSINESS_UNIT_ID =
  process.env.NEXT_PUBLIC_TRUSTPILOT_BUSINESS_UNIT_ID ?? "";

/**
 * TrustBox template ID — picks the widget style. Defaults to "Micro Combo"
 * (compact: star rating + TrustScore + review count), a good proof strip.
 * Override via NEXT_PUBLIC_TRUSTPILOT_TEMPLATE_ID to use a different TrustBox.
 */
export const TRUSTPILOT_TEMPLATE_ID =
  process.env.NEXT_PUBLIC_TRUSTPILOT_TEMPLATE_ID ?? "5419b732fbfb950b10de65e5";

/** The Trustpilot domain for this business (the review-page slug). */
export const TRUSTPILOT_DOMAIN = "thesitebook.co.uk";

/** Public Trustpilot review page — used as the widget's no-JS fallback link. */
export const TRUSTPILOT_REVIEW_URL =
  "https://uk.trustpilot.com/review/thesitebook.co.uk";

/** Widget locale. */
export const TRUSTPILOT_LOCALE = "en-GB";

/**
 * True only when a business-unit ID is configured. Gate every TrustBox render
 * on this so an unconfigured widget renders nothing rather than a broken or
 * empty embed.
 */
export function hasTrustpilot(
  businessUnitId: string = TRUSTPILOT_BUSINESS_UNIT_ID,
): boolean {
  return businessUnitId.trim().length > 0;
}
